import React, { FC, Suspense, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { commitLocalUpdate, graphql, useLazyLoadQuery, useMutation, useRelayEnvironment } from 'react-relay/hooks';
import config from 'src/shared/config';
import usePermissions from 'src/shared/hooks/usePermissions';
import { stringFormat, T, toLocalID } from 'src/shared/helpers';
import ChatIcon from 'src/shared/material_icons/communication/chat_bubble_outline.svg';
import DescriptionIcon from 'src/shared/material_icons/action/description.svg';
import PlayCircleIcon from 'src/shared/material_icons/av/play_circle_outline.svg';
import WarningAmberIcon from 'src/shared/material_icons/alert/warning_amber.svg';
import StorefrontIcon from 'src/shared/material_icons/places/storefront.svg';
import styled from 'styled-components';
import { CustomErrorBoundary } from '../customErrorBoundary';
import { Icon } from '../icon';
import { NotificationBanner } from '../notificationBanner';
import { Dialog, useDialogManager } from '../dialog';
import { Spinner } from '../spinner';
import { showErrorNotification, showWarningNotification } from '../notificationToaster';
import Stack from '../stack/stack';
import { SupportTicketDropdownTrigger } from '../supportTicketDropdown';
import { shopifyAppEmbedBannerMarkActiveMutation } from './__generated__/shopifyAppEmbedBannerMarkActiveMutation.graphql';
import { shopifyAppEmbedBannerQuery } from './__generated__/shopifyAppEmbedBannerQuery.graphql';
import { shopifyAppEmbedBannerStatusQuery } from './__generated__/shopifyAppEmbedBannerStatusQuery.graphql';

// The Needs attention row points here instead of repeating the instructions inside its card.
export const SHOPIFY_APP_EMBED_BANNER_ANCHOR_ID = 'shopify-app-embed-banner';

// The anchor matters: the article covers the whole Shopify setup, and this fragment lands on the
// app embed section, which is the only part the banner is asking for.
const SETUP_GUIDE_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/360024941034#h_01M233Z0KMT8WG8AQVHNJKFZZ1';

// The 20-second walkthrough of turning the app embed on, for merchants who would rather watch than read.
const SETUP_VIDEO_URL = 'https://www.youtube.com/watch?v=-ct2gPdid9M';

export const scrollToShopifyAppEmbedBanner = () => {
    const banner = document.getElementById(SHOPIFY_APP_EMBED_BANNER_ANCHOR_ID);
    if (!banner) return;

    banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

// The banners this one sits next to carry a close button, which insets their content on the right.
// This one must not be dismissible, so it reserves the same width instead and its actions line up
// with theirs rather than hanging 44px further out.
const EmbedBanner = styled(NotificationBanner)`
    > div {
        padding-right: 58px;
    }
`;

const BannerLinks = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 20px;
    margin-top: 10px;
`;

// Doubled specificity so these win over the banner's blanket `a` rule and the links all look alike,
// whether the trigger renders as a link or as a button.
const BannerLink = styled.a`
    && {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        border: 0;
        background: none;
        padding: 0;
        font-family: inherit;
        font-size: 12.5px;
        font-weight: 500;
        color: inherit;
        text-decoration: underline;
        cursor: pointer;

        &:hover {
            color: ${(props) => props.theme.app.secondary};
        }
    }

    ${Icon} {
        width: 15px;
        height: 15px;
    }
`;

// The shared banner keeps title and text on one line. Here the title carries the whole message, so
// it gets its own line — same type as NotificationBannerTitle, only as a block.
const BannerTitle = styled.strong`
    font-size: 13px;
    font-weight: 600;
    color: inherit;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 10px;
    margin-bottom: 2px;
`;

// The shop reads as a label rather than as part of the sentence: with several banners stacked, the
// store is what the merchant scans for, and inside the running text it gets lost.
const StoreChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 9px;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid #de1c22;
    color: #263238;
    font-size: 12px;
    font-weight: 600;
    overflow-wrap: anywhere;

    ${Icon} {
        width: 14px;
        height: 14px;
        color: #c4231b;
        flex: none;
    }
`;

// ── copy ────────────────────────────────────────────────────────────────────────────────────────

const getBannerText = () =>
    T._(
        'The app is installed, but it is not switched on inside your Shopify theme yet. Until it is, the Customize button never appears on your product pages: shoppers have no way into the customizer and cannot buy personalized products. This is the last step, and it takes a few seconds.',
        'Admin'
    );

const getBannerTitle = () => T._("Zakeke isn't live on this store yet", 'Admin');

const getBannerTurnOnLabel = () => T._('Turn Zakeke on in Shopify', 'Admin');

const getDoneLabel = () => T._("I've turned it on", 'Admin');

const getAskUsLabel = () => T._('Ask us to turn it on', 'Admin');

/** The short mark that flags an affected store in a list, a chip or a tab. */
export const getZakekeOffLabel = () => T._('Zakeke off', 'Admin');

// Two words cannot carry the state on their own, so wherever the mark appears it explains itself on
// hover and sends the merchant back to the banner, which is where the buttons are.
export const getZakekeOffTooltip = () =>
    T._(
        'Zakeke is installed on this store but not switched on inside the Shopify theme, so the Customize button does not appear on the product pages and shoppers cannot open the customizer. Use the banner at the top of the page to turn it on.',
        'Admin'
    );

// The merchant only has to hit Send: the request already says what it is about and for which store.
const getSupportMessage = (shop: string) =>
    stringFormat(T._('Please turn the Zakeke app embed on in my Shopify theme for {0}. I would rather you did it than do it myself.', 'Admin'), shop);

type PendingPhase = 'checking' | 'saving';
const setPendingPhase = (phases: Map<string, PendingPhase>, id: string, phase: PendingPhase | null) => {
    const next = new Map(phases);
    if (phase) next.set(id, phase);
    else next.delete(id);
    return next;
};

type AppEmbedStatus = shopifyAppEmbedBannerStatusQuery['response']['shopifyAppEmbedStatus'];

const AppEmbedStatusVerifier = ({ salesChannelID, onResult }: { salesChannelID: string; onResult: (status: AppEmbedStatus) => void }) => {
    const { shopifyAppEmbedStatus } = useLazyLoadQuery<shopifyAppEmbedBannerStatusQuery>(
        graphql`
            query shopifyAppEmbedBannerStatusQuery($salesChannelID: ID!) {
                shopifyAppEmbedStatus(salesChannelID: $salesChannelID)
            }
        `,
        { salesChannelID },
        { fetchPolicy: 'network-only' }
    );
    const hasReported = useRef(false);

    useEffect(() => {
        if (hasReported.current) return;
        hasReported.current = true;
        onResult(shopifyAppEmbedStatus);
    }, [onResult, shopifyAppEmbedStatus]);

    return null;
};

const AppEmbedStatusError = ({ onError }: { onError: () => void }) => {
    const hasReported = useRef(false);

    useEffect(() => {
        if (hasReported.current) return;
        hasReported.current = true;
        onError();
    }, [onError]);

    return null;
};

interface ShopifyAppEmbedBannerProps {
    onStoreConfirmed?: (remainingStores: number, salesChannelID: string) => void;
    salesChannelIDs?: readonly string[];
    compact?: boolean;
    className?: string;
    /** Limits the banner to one storefront. Omit to cover every store still switched off. */
    salesChannelID?: string | number | null;
}

interface AppEmbedStore {
    readonly salesChannelID: string;
    readonly shop: string;
    readonly appEmbedDeepLink: string;
}

/** Sales channel ids reach us as global guids in some queries and as plain ids in others. */
export const isSameSalesChannel = (left: string, right: string) => left === right || (toLocalID(left) !== null && toLocalID(left) === toLocalID(right));

// Confirmations the merchant has already given in this session. The backend does drop a store from
// shopifyAppEmbedsToEnable once it is marked active, but any query response that was already in
// flight still carries the old list and would write the store back into the Relay store. Keeping the
// confirmed ids here means a store that has been confirmed cannot come back, whatever lands later.
const confirmedSalesChannelIDs = new Set<string>();
const confirmationListeners = new Set<() => void>();

const subscribeToConfirmations = (listener: () => void) => {
    confirmationListeners.add(listener);
    return () => {
        confirmationListeners.delete(listener);
    };
};

const getConfirmationsSnapshot = () => confirmedSalesChannelIDs.size;

const rememberConfirmation = (salesChannelID: string) => {
    confirmedSalesChannelIDs.add(salesChannelID);
    confirmationListeners.forEach((listener) => listener());
};

export const isAppEmbedConfirmed = (salesChannelID: string) => [...confirmedSalesChannelIDs].some((confirmed) => isSameSalesChannel(confirmed, salesChannelID));

// Development aid. `shopifyMarkAppEmbedActive` is one-way — the schema has no mutation to put a store
// back among the ones to enable — so once every store is confirmed there is no way to see the notice
// again. This override stages stores as still off from the URL, sticks to the browser tab so the whole
// app can be walked without repeating it, and is inert in production. `?fakeAppEmbeds=off` clears it.
//
//   ?fakeAppEmbeds=1:store-a.myshopify.com,1061005:store-b.myshopify.com
const FAKE_APP_EMBEDS_PARAM = 'fakeAppEmbeds';

const readStagedParam = (): string | null => {
    const fromUrl = new URLSearchParams(window.location.search).get(FAKE_APP_EMBEDS_PARAM);

    try {
        if (fromUrl === 'off') {
            window.sessionStorage.removeItem(FAKE_APP_EMBEDS_PARAM);
            return null;
        }

        if (fromUrl) window.sessionStorage.setItem(FAKE_APP_EMBEDS_PARAM, fromUrl);

        return fromUrl ?? window.sessionStorage.getItem(FAKE_APP_EMBEDS_PARAM);
    } catch {
        return fromUrl;
    }
};

const getStagedAppEmbeds = (): AppEmbedStore[] => {
    if (config.isProduction || typeof window === 'undefined') return [];

    const raw = readStagedParam();
    if (!raw) return [];

    return raw
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((entry) => {
            const [id, shop] = entry.split(':');
            const localID = id.split('/').pop() ?? id;

            // An id that is not a number would stage a store no row, chip or tab can ever match.
            if (!/^\d+$/.test(localID)) return null;

            const shopName = shop || `store-${localID}.myshopify.com`;

            return {
                salesChannelID: `guid://Zakeke/SalesChannel/${localID}`,
                shop: shopName,
                appEmbedDeepLink: `https://${shopName}/admin/themes/current/editor?context=apps`
            };
        })
        .filter((entry): entry is AppEmbedStore => entry !== null);
};

const isStagedAppEmbed = (salesChannelID: string) => getStagedAppEmbeds().some((fake) => isSameSalesChannel(fake.salesChannelID, salesChannelID));

/** Drops the stores already confirmed in this session, for any panel that reads shopifyAppEmbedsToEnable. */
export const usePendingAppEmbeds = <T extends { readonly salesChannelID: string }>(embeds: ReadonlyArray<T>): T[] => {
    useSyncExternalStore(subscribeToConfirmations, getConfirmationsSnapshot);

    const staged = getStagedAppEmbeds().filter((fake) => !embeds.some((embed) => isSameSalesChannel(embed.salesChannelID, fake.salesChannelID)));

    // Staged entries carry every field the real ones do; callers that read only salesChannelID ignore the rest.
    return [...(staged as unknown as T[]), ...embeds].filter((embed) => !isAppEmbedConfirmed(embed.salesChannelID));
};

const _ShopifyAppEmbedBanner: FC<ShopifyAppEmbedBannerProps> = ({ salesChannelID, salesChannelIDs, compact, className, onStoreConfirmed }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [pendingPhasesBySalesChannelID, setPendingPhasesBySalesChannelID] = useState<Map<string, PendingPhase>>(() => new Map());
    const environment = useRelayEnvironment();
    const { shopifyAppEmbedsToEnable } = useLazyLoadQuery<shopifyAppEmbedBannerQuery>(
        graphql`
            query shopifyAppEmbedBannerQuery {
                shopifyAppEmbedsToEnable {
                    salesChannelID
                    shop
                    appEmbedDeepLink
                }
            }
        `,
        {},
        { fetchPolicy: 'store-and-network' }
    );

    const [markAppEmbedActive] = useMutation<shopifyAppEmbedBannerMarkActiveMutation>(graphql`
        mutation shopifyAppEmbedBannerMarkActiveMutation($salesChannelID: ID!) {
            shopifyMarkAppEmbedActive(salesChannelID: $salesChannelID)
        }
    `);

    const pendingEmbeds = usePendingAppEmbeds(shopifyAppEmbedsToEnable);

    const [openedSalesChannelIDs, setOpenedSalesChannelIDs] = useState<Set<string>>(() => new Set());

    // Scoped to one storefront when a caller is already showing that store's settings; unscoped everywhere else.
    const stores = pendingEmbeds.filter(
        (store) =>
            (!salesChannelID || isSameSalesChannel(store.salesChannelID, String(salesChannelID))) &&
            (!salesChannelIDs || salesChannelIDs.some((id) => isSameSalesChannel(store.salesChannelID, id)))
    );

    // Two things have to happen on a confirmation. The session set is what actually keeps the store
    // away, since it survives every later query response; the Relay write is what makes the Needs
    // attention row react at once, without waiting for its own refetch.
    const forgetStore = (id: string) => {
        rememberConfirmation(id);

        commitLocalUpdate(environment, (store) => {
            const root = store.getRoot();
            const embeds = root.getLinkedRecords('shopifyAppEmbedsToEnable');
            if (!embeds) return;

            root.setLinkedRecords(
                embeds.filter((embed) => !isSameSalesChannel(String(embed.getValue('salesChannelID')), id)),
                'shopifyAppEmbedsToEnable'
            );
        });
        onStoreConfirmed?.(stores.filter((store) => !isAppEmbedConfirmed(store.salesChannelID)).length, id);
    };

    const handleMarkActive = (salesChannelID: string) => {
        if (isStagedAppEmbed(salesChannelID)) {
            forgetStore(salesChannelID);
            return;
        }
        setPendingPhasesBySalesChannelID((currentPhases) => setPendingPhase(currentPhases, salesChannelID, 'checking'));
    };

    const handleVerificationError = (salesChannelID: string) => {
        setPendingPhasesBySalesChannelID((currentPhases) => setPendingPhase(currentPhases, salesChannelID, null));
        showErrorNotification({ message: T._('Unable to update the Shopify app embed status.', 'Admin') });
    };

    const handleStatus = async (salesChannelID: string, status: AppEmbedStatus) => {
        const setPhase = (phase: PendingPhase | null) =>
            setPendingPhasesBySalesChannelID((currentPhases) => setPendingPhase(currentPhases, salesChannelID, phase));

        try {
            // NOT_APPLICABLE means the app embed cannot be verified (for example the store did not grant the
            // read_themes permission), so the merchant confirmation is still trusted.
            if (status === 'DISABLED') {
                showWarningNotification({
                    message: T._(
                        'Zakeke is still not active on your Shopify storefront. In the theme editor, turn on the Zakeke app embed and click Save, then confirm again.',
                        'Admin'
                    )
                });
                return;
            }

            setPhase('saving');

            const { promise, resolve, reject } = Promise.withResolvers<void>();
            markAppEmbedActive({
                variables: { salesChannelID },
                onCompleted: (response) => {
                    if (!response.shopifyMarkAppEmbedActive) {
                        reject(new Error('Unable to update the Shopify app embed status.'));
                        return;
                    }

                    resolve();
                },
                onError: reject
            });
            await promise;

            forgetStore(salesChannelID);
        } catch {
            showErrorNotification({ message: T._('Unable to update the Shopify app embed status.', 'Admin') });
        } finally {
            setPhase(null);
        }
    };

    const openStore = (store: AppEmbedStore) => {
        setOpenedSalesChannelIDs((currentIDs) => new Set(currentIDs).add(store.salesChannelID));
        window.open(store.appEmbedDeepLink, '_blank', 'noopener,noreferrer');
    };

    // Each banner names its own shop so the setup and confirmation actions refer to the same store.
    if (stores.length === 0) return null;

    if (compact) {
        return (
            <Stack className={className}>
                <NotificationBanner
                    type='CRITICAL'
                    title={
                        stores.length === 1
                            ? T._('Enable Zakeke on your Shopify store', 'Admin')
                            : stringFormat(T._('Enable Zakeke on your Shopify stores', 'Admin'), stores.length.toString())
                    }
                    content={
                        stores.length === 1
                            ? T._(
                                  'A connected Shopify store still needs Zakeke to be enabled. Enable the Zakeke app embed in its theme to make published products usable on its storefront.',
                                  'Admin'
                              )
                            : stringFormat(
                                  T._(
                                      '{0} connected Shopify stores still need Zakeke to be enabled. Enable the Zakeke app embed in each store listed to make published products usable on its storefront.',
                                      'Admin'
                                  ),
                                  stores.length.toString()
                              )
                    }
                    buttons={[
                        {
                            label: T._('Enable Zakeke on Shopify', 'Admin'),
                            onClick: () =>
                                showDialog(
                                    'test-page-shopify-app-embed',
                                    <Dialog size='large' closeOnOverlayClick>
                                        <ShopifyAppEmbedBanner
                                            salesChannelIDs={stores.map((store) => store.salesChannelID)}
                                            onStoreConfirmed={(remainingStores, salesChannelID) => {
                                                onStoreConfirmed?.(remainingStores, salesChannelID);
                                                if (remainingStores === 0) closeDialog('test-page-shopify-app-embed');
                                            }}
                                        />
                                    </Dialog>
                                )
                        }
                    ]}
                />
            </Stack>
        );
    }

    return (
        <Stack className={className} id={SHOPIFY_APP_EMBED_BANNER_ANCHOR_ID} $gap='12px'>
            {[...pendingPhasesBySalesChannelID]
                .filter(([, phase]) => phase === 'checking')
                .map(([salesChannelID]) => (
                    <CustomErrorBoundary key={salesChannelID} render={() => <AppEmbedStatusError onError={() => handleVerificationError(salesChannelID)} />}>
                        <Suspense fallback={null}>
                            <AppEmbedStatusVerifier salesChannelID={salesChannelID} onResult={(status) => handleStatus(salesChannelID, status)} />
                        </Suspense>
                    </CustomErrorBoundary>
                ))}

            {stores.map((store) => (
                <EmbedBanner
                    key={store.salesChannelID}
                    type='CRITICAL'
                    icon={
                        <Icon>
                            <WarningAmberIcon />
                        </Icon>
                    }
                    content={
                        <>
                            <TitleRow>
                                <BannerTitle>{getBannerTitle()}</BannerTitle>
                                <StoreChip>
                                    <Icon aria-hidden='true'>
                                        <StorefrontIcon />
                                    </Icon>
                                    {store.shop}
                                </StoreChip>
                            </TitleRow>
                            {getBannerText()}
                            <BannerLinks>
                                <SupportTicketDropdownTrigger placement='top-start' initialMessage={getSupportMessage(store.shop)}>
                                    {({ onClick, isOpen }) => (
                                        <BannerLink as='button' type='button' aria-expanded={isOpen} onClick={onClick}>
                                            <Icon aria-hidden='true'>
                                                <ChatIcon />
                                            </Icon>
                                            {getAskUsLabel()}
                                        </BannerLink>
                                    )}
                                </SupportTicketDropdownTrigger>
                                <BannerLink href={SETUP_GUIDE_URL} target='_blank' rel='noopener noreferrer'>
                                    <Icon aria-hidden='true'>
                                        <DescriptionIcon />
                                    </Icon>
                                    {T._('Step-by-step guide', 'Admin')}
                                </BannerLink>
                                <BannerLink href={SETUP_VIDEO_URL} target='_blank' rel='noopener noreferrer'>
                                    <Icon aria-hidden='true'>
                                        <PlayCircleIcon />
                                    </Icon>
                                    {T._('Watch a 20-second video', 'Admin')}
                                </BannerLink>
                            </BannerLinks>
                        </>
                    }
                    buttons={[
                        // The confirmation only appears once the merchant has been sent to Shopify:
                        // before that there is nothing to confirm. Turn on never goes away, so an
                        // interrupted attempt can always be picked up again.
                        ...(openedSalesChannelIDs.has(store.salesChannelID)
                            ? [
                                  {
                                      label:
                                          pendingPhasesBySalesChannelID.get(store.salesChannelID) === 'checking'
                                              ? T._('Checking on Shopify…', 'Admin')
                                              : pendingPhasesBySalesChannelID.get(store.salesChannelID) === 'saving'
                                                ? T._('Saving…', 'Admin')
                                                : getDoneLabel(),
                                      outline: true,
                                      disabled: pendingPhasesBySalesChannelID.has(store.salesChannelID),
                                      onClick: () => handleMarkActive(store.salesChannelID)
                                  }
                              ]
                            : []),
                        {
                            label: getBannerTurnOnLabel(),
                            onClick: () => openStore(store)
                        }
                    ]}
                />
            ))}
        </Stack>
    );
};

const ShopifyAppEmbedBanner: FC<ShopifyAppEmbedBannerProps> = (props) => {
    const { currentIAMUser } = usePermissions();
    if (currentIAMUser.isIAMUser) return null;

    return (
        <CustomErrorBoundary render={() => null}>
            <Suspense
                fallback={
                    props.compact ? null : (
                        <Stack className={props.className}>
                            <Spinner />
                        </Stack>
                    )
                }
            >
                <_ShopifyAppEmbedBanner {...props} />
            </Suspense>
        </CustomErrorBoundary>
    );
};

export default ShopifyAppEmbedBanner;
